import { x } from '@xstyled/styled-components';
import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { t } from 'ttag';
import { select } from '~/lib/select';
import { Bar } from '~/training/progress-bar/ProgressBar.styles';
import { usePhoneAudioEffect } from '~/ui/audio-effect/usePhoneAudioEffect';
import { Flex } from '~/ui/flex/Flex';
import { Tooltip } from '~/ui/tooltip/Tooltip';
import { MediaCaptions } from '~/ui/video-player/MediaCaptions';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { shallow } from 'zustand/shallow';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import { TrainingState, useTrainingStore } from '~/training/state';
import { useEditorStore } from '~/shell/Store';
import FilePickerV2 from '~/editor/filePicker/FilePicker_v2';
import Avatar from './avatar.png';
import * as S from './styles';

const selectorTraining = (s: TrainingState) => select(s, 'logoUrl');
const selector = (s: MediaPlayerStore) =>
  select(s, 'pause', 'play', 'progress', 'status', 'reset', 'loadMedia');

interface Props {
  id?: string;
  children: any;
  narration: string;
  subtitlesUrl?: string;
  autoPlay: boolean;
  showProgress?: boolean;
  name?: string;
  photo?: string;
  personalization?: boolean;
}

export const PhoneCall = ({
  id,
  children,
  narration,
  subtitlesUrl,
  showProgress,
  name,
  photo,
  personalization = false
}: Props) => {
  const avatarRef = useRef<HTMLDivElement | null>(null);
  const { logoUrl } = useTrainingStore(selectorTraining, shallow);
  const { contentEditable, updateFile } = useEditorStore();
  const [showFilePicker, setShowFilePicker] = useState(false);

  const [hasStarted, setHasStarted] = useState(false);
  const startTimeoutRef = useRef<number | null>(null);
  const audioEffectPlayedRef = useRef<boolean>(false);
  const { play, pause, progress, status, loadMedia } = useMediaPlayerStore(
    selector,
    shallow
  );

  const {
    audioEffectDuration,
    phoneAudioEffectIsPaused,
    pausePhoneAudioEffect
  } = usePhoneAudioEffect(contentEditable);

  useGlobalMedia();

  useEffect(() => {
    pause();
  }, []);

  useEffect(() => {
    // Use setTimeout(0) to avoid audio loading issues during slide transitions.
    // This pushes loadMedia to the next event loop cycle, allowing React cleanup
    // from previous slides to complete before loading new media
    setTimeout(() => {
      loadMedia(narration, subtitlesUrl, 'audio', true, 0);
    }, 0);
  }, [narration, subtitlesUrl]);

  useEffect(() => {
    if (hasStarted) {
      pausePhoneAudioEffect();
    }
  }, [hasStarted]);

  useEffect(() => {
    if (status === 'playing' && !audioEffectPlayedRef.current) {
      audioEffectPlayedRef.current = true;
      pause();
    }

    if (
      startTimeoutRef.current === null &&
      !phoneAudioEffectIsPaused &&
      !hasStarted &&
      status === 'paused'
    ) {
      startTimeoutRef.current = window.setTimeout(() => {
        setHasStarted(true);
        play();
      }, audioEffectDuration);
    }

    return () => {
      if (status === 'paused') {
        setHasStarted(true);
      }

      if (startTimeoutRef.current !== null) {
        clearTimeout(startTimeoutRef.current as number);
        startTimeoutRef.current = null;
      }
    };
  }, [status]);

  const UpdateFileElement = async (file: any) => {
    if (id) {
      updateFile(id, file, 'PHONE');
    }
    setShowFilePicker(false);
  };

  return (
    <Flex h="100%" alignItems="center" justifyContent="center">
      <S.Phone>
        <Flex marginLeft="20px;">
          <div
            ref={avatarRef}
            style={{ display: 'inline-block', position: 'relative' }}
          >
            {photo || (personalization && logoUrl) ? (
              <x.img
                src={photo || logoUrl}
                w="33px"
                h="33px"
                borderRadius="50px"
                backgroundColor="#FFF"
                alt={''}
                onClick={
                  contentEditable ? () => setShowFilePicker(true) : undefined
                }
                style={contentEditable ? { cursor: 'pointer' } : undefined}
              />
            ) : (
              <x.img
                src={Avatar}
                w="33px"
                h="33px"
                borderRadius="50px"
                backgroundColor="#FFF"
                alt={''}
                onClick={
                  contentEditable ? () => setShowFilePicker(true) : undefined
                }
                style={contentEditable ? { cursor: 'pointer' } : undefined}
              />
            )}

            {showFilePicker &&
              id &&
              createPortal(
                <FilePickerV2
                  fileElRef={avatarRef}
                  updateFile={UpdateFileElement}
                  type="image"
                  onClose={() => setShowFilePicker(false)}
                />,
                document.body
              )}
          </div>

          <x.div margin="-3px 0 0 8px;">
            <S.UserName>
              {children[0][0].props?.value ? children : name}
            </S.UserName>
            <S.UserLabel>Connecting...</S.UserLabel>
          </x.div>
        </Flex>

        <S.CallButton
          aria-label={t`Accept`}
          aria-labelledby={t`Click "Accept" to answer the call`}
          tabIndex={1}
          onClick={() => {
            if (!hasStarted) {
              play();
              setHasStarted(true);
            }
          }}
        />

        {!hasStarted && !contentEditable && (
          <Tooltip top="350px" right="73px" placement={'bottom-right'}>
            {t`Click "Accept" to answer the call`}
          </Tooltip>
        )}
        {showProgress && (
          <S.ProgressWrapper>
            <Bar bg="greyDark">
              <Bar bg="white" w={`${progress * 100}%`} />
            </Bar>
          </S.ProgressWrapper>
        )}
      </S.Phone>

      <MediaCaptions />
    </Flex>
  );
};
